import { spawn as spawnChildProcess, type ChildProcess } from "node:child_process";
import { appendFileSync, statSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { reportAgentCrash } from "./crash-report.js";
import type { BootLogger } from "./types.js";

/**
 * Ports the supervised launch loop (entrypoint.sh:901-978): spawn
 * `conveyor-agent`, restart it on a nonzero exit up to `AGENT_CRASH_MAX`
 * times with a backoff between attempts, and report every crash to the API.
 * A clean exit (0) or an aborted (SIGTERM) supervision both return without
 * further restarts.
 *
 * Liveness contract: this supervisor writes the markers
 * `apps/api/.../dockerfiles/liveness.sh` reads — `LAUNCH_MARKER`
 * (/tmp/conveyor-agent-launching) once supervision starts (post-bind, before
 * the first spawn), arming the probe, and `RESTARTING_MARKER` touched before
 * each restart backoff so the probe stays green across backoff sleeps.
 * (Same names the late-era bash entrypoint used, so one liveness.sh serves
 * both worlds during the rollout window.)
 */
// Mirrors bootstrap-route.ts:35.
export const AGENT_CRASH_MAX = 3;
export const AGENT_LOG_PATH = "/tmp/claudespace-agent.log";
// Cap the agent log so a long-lived, chatty pod can't fill the 10Gi ephemeral
// disk and get evicted. Rotation is a single truncate-on-overflow (no rolling
// N files): pod stdout already tees the full stream, so the file is only a
// same-pod convenience tail, not the archive.
export const AGENT_LOG_MAX_BYTES = 16 * 1024 * 1024;

const LAUNCH_MARKER_NAME = "conveyor-agent-launching";
const RESTARTING_MARKER_NAME = "conveyor-agent-restarting";
export const LAUNCH_MARKER = `/tmp/${LAUNCH_MARKER_NAME}`;
export const RESTARTING_MARKER = `/tmp/${RESTARTING_MARKER_NAME}`;

const DEFAULT_CRASH_BACKOFF_MS = 10_000;
// Under the pod's 60s terminationGracePeriodSeconds.
const DEFAULT_DRAIN_TIMEOUT_MS = 45_000;
const DEFAULT_MARKER_DIR = "/tmp";

export interface SuperviseOptions {
  childEnv: Record<string, string>;
  cwd: string;
  apiUrl: string;
  bootstrapToken: string;
  log: BootLogger;
  /** Injected in tests to substitute a fake binary for `conveyor-agent`. */
  spawnFn?: typeof spawnChildProcess;
  reportFn?: typeof reportAgentCrash;
  crashBackoffMs?: number;
  drainTimeoutMs?: number;
  /** default "/tmp"; tests point this at a tmpdir. */
  markerDir?: string;
  /**
   * Test-visible SIGTERM stand-in. Production wiring (`process.on("SIGTERM",
   * () => controller.abort())`) happens in Task 9 — this module only reacts
   * to the signal, it never installs the process listener itself.
   */
  signal?: AbortSignal;
}

/** Touch (create-or-refresh) a liveness marker. Best-effort — a marker-dir
 * permission problem must never take down agent supervision. */
function touchMarker(path: string, log: BootLogger): void {
  try {
    writeFileSync(path, "");
  } catch (err) {
    log.warn(
      `[boot] WARN failed to write liveness marker ${path}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/**
 * Build a size-bounded appender for the agent log. Tracks the running file size
 * (seeded from any pre-existing file so a restart doesn't forget prior growth)
 * and, once the next chunk would cross `maxBytes`, truncates the file and starts
 * fresh with that chunk rather than growing without bound. Best-effort
 * throughout (`report_agent_crash`-style `|| true`): a full disk or permission
 * error must never kill supervision.
 */
export function createLogAppender(path: string, maxBytes: number): (chunk: Buffer) => void {
  let size = 0;
  try {
    size = statSync(path).size;
  } catch {
    size = 0;
  }
  return (chunk: Buffer): void => {
    try {
      if (size + chunk.length > maxBytes) {
        // Rotate by truncation — drop the old log and start over with this
        // chunk (pod stdout still carries the full stream).
        writeFileSync(path, chunk);
        size = chunk.length;
      } else {
        appendFileSync(path, chunk);
        size += chunk.length;
      }
    } catch {
      // Best-effort log file — see doc comment above.
    }
  };
}

/**
 * Tees the child's stdout/stderr to our own stdout (so pod logs still show
 * it) and appends to the size-bounded agent log via `appendLog`. Chunks are
 * written as they arrive — no ordering guarantee is claimed or needed beyond
 * "as they arrive".
 */
function teeChildOutput(child: ChildProcess, appendLog: (chunk: Buffer) => void): void {
  const onData = (chunk: Buffer): void => {
    process.stdout.write(chunk);
    appendLog(chunk);
  };
  child.stdout?.on("data", onData);
  child.stderr?.on("data", onData);
}

function spawnAgent(spawnFn: typeof spawnChildProcess, opts: SuperviseOptions): ChildProcess {
  // argv `[]`, PATH-resolved (no shell) so a preflight npm-install update
  // lands on the next spawn without any code here caring about it.
  return spawnFn("conveyor-agent", [], {
    cwd: opts.cwd,
    env: opts.childEnv,
    stdio: ["ignore", "pipe", "pipe"],
  });
}

/** Resolves the child's exit code. A signal-kill (code null) counts as
 * nonzero — the only caller that kills the child itself (drain) already
 * short-circuits before this matters for restart bookkeeping. */
function waitForExit(child: ChildProcess): Promise<number> {
  return new Promise((resolve) => {
    child.once("exit", (code, signal) => {
      resolve(code ?? (signal ? 1 : 0));
    });
    child.once("error", () => resolve(1));
  });
}

/** Never resolves unless `signal` fires (or is already aborted). */
function whenAborted(signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve) => {
    if (!signal) return;
    if (signal.aborted) {
      resolve();
      return;
    }
    signal.addEventListener("abort", () => resolve(), { once: true });
  });
}

/**
 * Sleeps `ms`, resolving early if `signal` aborts — mirrors poll.ts's
 * module-private `sleepUnlessAborted` so a SIGTERM landing mid-backoff
 * doesn't ride out the full `crashBackoffMs` before draining can start.
 */
function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  if (!signal) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const onAbort = (): void => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Forward SIGTERM to the child, wait up to `drainTimeoutMs` for it to exit
 * on its own, then SIGKILL. Always resolves 0 — draining is a clean
 * shutdown from the supervisor's point of view regardless of the child's
 * final exit code.
 */
async function drainChild(
  child: ChildProcess,
  exitPromise: Promise<number>,
  drainTimeoutMs: number,
  log: BootLogger,
): Promise<number> {
  log.info("[boot] SIGTERM received, draining agent...");
  child.kill("SIGTERM");

  const TIMED_OUT = Symbol("drain-timeout");
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), drainTimeoutMs);
  });
  const winner = await Promise.race([exitPromise, timeout]);
  if (timer) clearTimeout(timer);

  if (winner === TIMED_OUT) {
    log.warn(`[boot] agent did not exit within drainTimeoutMs=${drainTimeoutMs}ms — SIGKILL`);
    child.kill("SIGKILL");
    await exitPromise;
  }
  return 0;
}

export async function superviseRunner(opts: SuperviseOptions): Promise<number> {
  const spawnFn = opts.spawnFn ?? spawnChildProcess;
  const reportFn = opts.reportFn ?? reportAgentCrash;
  const crashBackoffMs = opts.crashBackoffMs ?? DEFAULT_CRASH_BACKOFF_MS;
  const drainTimeoutMs = opts.drainTimeoutMs ?? DEFAULT_DRAIN_TIMEOUT_MS;
  const markerDir = opts.markerDir ?? DEFAULT_MARKER_DIR;
  const launchMarkerPath = join(markerDir, LAUNCH_MARKER_NAME);
  const restartingMarkerPath = join(markerDir, RESTARTING_MARKER_NAME);

  // Supervision starting (post-bind) arms the liveness probe — see the
  // liveness-contract doc comment above.
  touchMarker(launchMarkerPath, opts.log);

  // One size-bounded appender shared across every restart so the cap tracks the
  // whole supervision lifetime, not a single spawn.
  const appendLog = createLogAppender(AGENT_LOG_PATH, AGENT_LOG_MAX_BYTES);

  let attempt = 0;
  while (true) {
    attempt++;
    opts.log.info("[boot] Launching agent...");
    const child = spawnAgent(spawnFn, opts);
    teeChildOutput(child, appendLog);
    const exitPromise = waitForExit(child);

    const winner = await Promise.race([
      exitPromise.then((code) => ({ type: "exit" as const, code })),
      whenAborted(opts.signal).then(() => ({ type: "abort" as const })),
    ]);

    if (winner.type === "abort") {
      return await drainChild(child, exitPromise, drainTimeoutMs, opts.log);
    }

    const exitCode = winner.code;
    if (exitCode === 0) {
      opts.log.info("[boot] agent exited cleanly (code 0), shutting down pod.");
      return 0;
    }

    if (attempt >= AGENT_CRASH_MAX) {
      opts.log.error(
        `[boot] agent crashed (code ${exitCode}) — attempt cap ${AGENT_CRASH_MAX} reached, giving up.`,
      );
      await reportFn(opts.apiUrl, opts.bootstrapToken, { exitCode, attempt, final: true });
      return 1;
    }

    touchMarker(restartingMarkerPath, opts.log);
    await reportFn(opts.apiUrl, opts.bootstrapToken, { exitCode, attempt, final: false });
    opts.log.warn(
      `[boot] agent crashed (code ${exitCode}), retrying in ${crashBackoffMs}ms (attempt ${attempt}/${AGENT_CRASH_MAX})...`,
    );
    await sleepUnlessAborted(crashBackoffMs, opts.signal);
    if (opts.signal?.aborted) {
      // Aborted during backoff — no child is running; draining means "stop
      // trying to restart", nothing to SIGTERM/await here.
      return 0;
    }
  }
}
